import { Dispatch, SetStateAction, useCallback, useEffect, useMemo } from 'react';
import { useConfigsContext } from '@/context/ConfigsContext';
import { getStreamUris } from '@/lib/blendvision';
import { updateProfileMeByCmsAccountId } from '@/server/lib/profile';
import { createPlaybackToken } from '@/server/services/blendvision';
import { getFile, getVod } from '@/services/blendvision';
import { Asset } from '@/types/asset';
export const useFetchBlendVisionVodInfo = (assets: Asset[], setAssets: Dispatch<SetStateAction<Asset[]>>, profile: 'public' | 'private') => {
  const {
    store,
    setStore
  } = useConfigsContext();
  const htcToken = store?.credentials?.htcToken;
  const cmsAccountId = store?.profile?.id;
  const medias = store?.profile?.medias;
  const noUrlVodAssets = useMemo(() => assets.filter(asset => asset.video && asset.video.vodId && !asset.video.isConverted && !asset.video.isFetching && !asset.video.url), [assets]);
  const handleFetchBlendVisionVodInfo = useCallback(async () => {
    if (noUrlVodAssets.length === 0) return;
    const promises = noUrlVodAssets.map(async asset_0 => {
      setAssets(prev => prev.map(a => a.video && a.video?.vodId === asset_0.video?.vodId ? {
        ...a,
        video: {
          ...a.video,
          isFetching: true
        }
      } : a));
      const vodResponse = await getVod(asset_0.video!.vodId);
      if ('vod' in vodResponse) {
        const vod = vodResponse.vod;
        const fileId = vod.source.library.video.id;
        const coverImage = vod.cover_image?.auto.url;
        const isConverted = vod.status === 'VOD_STATUS_SUCCEEDED';
        const {
          download_uri
        } = await getFile(fileId);
        const {
          dashUri,
          hlsUri
        } = getStreamUris(vod.stream);
        if (profile === 'private') {
          setStore(prev_0 => ({
            ...prev_0,
            profile: {
              ...prev_0.profile!,
              medias: (prev_0.profile?.medias || [])?.map(media => media.video?.vod_id === asset_0.video?.vodId ? {
                ...media,
                video: {
                  ...media.video!,
                  cover_image: coverImage,
                  file_id: fileId
                }
              } : media)
            }
          }));
        }
        if (profile === 'public' && isConverted) {
          const playbackToken = await createPlaybackToken({
            resourceId: asset_0.video!.vodId,
            resourceType: 'RESOURCE_TYPE_VOD_EVENT'
          });
          const token = playbackToken?.token;
          setAssets(prev_1 => prev_1.map(a_0 => a_0.video?.vodId === asset_0.video?.vodId ? {
            ...a_0,
            video: {
              ...a_0.video!,
              ...(coverImage && {
                coverImage
              }),
              url: download_uri,
              fileId,
              isFetching: false,
              playbackToken: token,
              isConverted,
              title: vod.name,
              ...(dashUri && {
                dashUri
              }),
              ...(hlsUri && {
                hlsUri
              })
            }
          } : a_0));
        } else {
          setAssets(prev_2 => prev_2.map(a_1 => a_1.video && a_1.video?.vodId === asset_0.video?.vodId ? {
            ...a_1,
            video: {
              ...a_1.video,
              ...(coverImage && {
                coverImage
              }),
              url: download_uri,
              fileId,
              isFetching: false,
              isConverted,
              title: vod.name,
              ...(dashUri && {
                dashUri
              }),
              ...(hlsUri && {
                hlsUri
              })
            }
          } : a_1));
        }
      } else if (htcToken && cmsAccountId) {
        // 🥷🏼 handle unhandled error if vod error
        void updateProfileMeByCmsAccountId(cmsAccountId, {
          profile_media: medias?.filter(media_0 => media_0.video?.vod_id !== asset_0.video?.vodId)
        }).catch(() => {}).finally(() => {
          setAssets(prev_3 => prev_3.filter(a_2 => a_2.video?.vodId !== asset_0.video?.vodId));
          setStore(prev_4 => ({
            ...prev_4,
            profile: {
              ...prev_4.profile!,
              medias: prev_4.profile?.medias?.filter(media_1 => media_1.video?.vod_id !== asset_0.video?.vodId) || []
            }
          }));
        });
      }
    });
    await Promise.all(promises);
  }, [cmsAccountId, htcToken, medias, noUrlVodAssets, profile, setAssets, setStore]);
  useEffect(() => {
    handleFetchBlendVisionVodInfo();
  }, [handleFetchBlendVisionVodInfo]);
};